import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { IconButton, Skeleton, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";
import { GridFilterModel } from "@mui/x-data-grid";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";
import { useToast } from "../../context/Toast";
import {
  registerUser,
  searchUsers,
  toggleUserStatus,
  resendConfirmationCode,
  sendSurveys,
  getUserById,
} from "../../services/user/user";
import { searchGroups } from "../../services/group/group";
import { getApiErrorMessage } from "../../services/utils/apiError";
import UserTable, { UserTableModel } from "../../components/dataTable/userTable";
import ModalAddUser from "./modalUser";
import { User } from "../../interfaces/user.interface";
import { GroupDataFromSearchResult as GroupSearchResult } from "../../interfaces/group.interface";
import {
  ContainerConfigUser,
  InputWrapper,
  ContainerFilterButton,
} from "./styles";
import ModalUserGroups from "../../components/modais/modalUserGroups";
import TableSkeleton from "../../components/skelletons/settings/TableSkeleton";
import ModalEditUser from "../../components/modais/modalEditUserInfo";
import ModalUserInactivationReason from "../../components/modais/modalUserInactivationReason/index";
import UserCounters from "./components/userCounters";
import { UserStatus } from "../../enums/user";
import LicenseDisclaimerModal from "./components/licenseDisclaimerModal";
import { INACTIVATION_REASONS } from "../../data/inactivationReasons";

type ErrorHandlerOptions = {
  setLoading?: (loading: boolean) => void;
  redirectTo?: string;
  context?: "admin" | "user" | string;
};

type StatusFilterValue = "active" | "invited" | "inactive";
type LicenseDisclaimerMode = "add-user" | "reactivate-user";
const DEFAULT_STATUS_FILTER_VALUES: StatusFilterValue[] = ["active", "invited"];

const getEntityId = (id: unknown): string => String(id ?? "");

export default function Users() {
  const { t } = useTranslation('settings');
  const [term, setTerm] = useState("");
  const [listData, setListData] = useState({
    total_items: 0,
    total_pages: 0,
    current_page: 1,
    data: [] as User[],
  });

  const [controlLoading, setControlLoading] = useState(false);
  const { user, SignOut } = useAuth();
  const { currentCompany, refreshCurrentCompany } = useCompany();
  const { showToast } = useToast();

  const [userList, setUserList] = useState<User[]>([]);
  const [groupList, setGroupList] = useState<GroupSearchResult[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [thereIsUpdates, setThereIsUpdates] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);

  const [showModalGroup, setShowModalGroup] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserTableModel | null>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [showInactivationModal, setShowInactivationModal] = useState(false);
  const [userToChangeStatus, setUserToChangeStatus] = useState<UserTableModel | null>(null);
  const [showLicenseDisclaimer, setShowLicenseDisclaimer] = useState(false);
  const [licenseDisclaimerMode, setLicenseDisclaimerMode] = useState<LicenseDisclaimerMode | null>(null);
  const [pendingReactivateUser, setPendingReactivateUser] = useState<UserTableModel | null>(null);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [
      {
        id: "status-filter",
        field: "status",
        operator: "isAnyOf",
        value: DEFAULT_STATUS_FILTER_VALUES,
      },
    ],
  });
  const [modalGroupUser, setModalGroupUser] = useState<User | null>(null);

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [debouncedTerm, setDebouncedTerm] = useState("");

  const openUser = Boolean(anchorElUser);
  const currentCompanyId = currentCompany?.id ?? user.company_visited?.id ?? null;
  const isDefaultStatusFilterApplied =
    filterModel.items.length === 1 &&
    filterModel.items[0]?.field === "status" &&
    filterModel.items[0]?.operator === "isAnyOf" &&
    Array.isArray(filterModel.items[0]?.value) &&
    filterModel.items[0]?.value.length === DEFAULT_STATUS_FILTER_VALUES.length &&
    DEFAULT_STATUS_FILTER_VALUES.every((value) => filterModel.items[0]?.value.includes(value));

  const showGeneralLoadingSkeleton =
    loadingTable && term === "" && debouncedTerm === "" && isDefaultStatusFilterApplied;
  const requesterDisplayNameCacheRef = useRef<Record<string, string>>({});
  const requesterDisplayNamePendingRef = useRef<Record<string, Promise<string>>>({});

  const { activeUsers, invitedUsers, inactiveUsers } = useMemo(() => {
    return {
      activeUsers: userList.filter((item) => item.status === UserStatus.active).length,
      invitedUsers: userList.filter((item) => item.status === UserStatus.invited).length,
      inactiveUsers: userList.filter(
        (item) =>
          item.status === UserStatus.inactive ||
          item.status === UserStatus.invited_inactive
      ).length,
    };
  }, [userList]);

  const selectedInactivationReasonCodes = useMemo(() => {
    const reasonFilterItem = filterModel.items.find((item) => item.field === "inactivationReasonCode");

    if (
      reasonFilterItem?.operator === "isAnyOf" &&
      Array.isArray(reasonFilterItem.value)
    ) {
      return reasonFilterItem.value.filter(
        (value): value is string =>
          typeof value === "string" &&
          INACTIVATION_REASONS.some((reason) => reason.reasonCode === value)
      );
    }

    return [];
  }, [filterModel]);

  const selectedStatusValues = useMemo(() => {
    const statusFilterItem = filterModel.items.find((item) => item.field === "status");

    if (statusFilterItem?.operator === "isAnyOf" && Array.isArray(statusFilterItem.value)) {
      return statusFilterItem.value.filter(
        (value): value is StatusFilterValue =>
          value === "active" || value === "invited" || value === "inactive"
      );
    }

    return [];
  }, [filterModel]);

  const isStatusFilterSelected = (value: StatusFilterValue) =>
    selectedStatusValues.includes(value);

  const availableLicenses = useMemo(() => {
    const acquired = Number(currentCompany?.number_license_acquired ?? 0);
    const used = activeUsers + invitedUsers;
    return Math.max(acquired - used, 0);
  }, [currentCompany?.number_license_acquired, activeUsers, invitedUsers]);

  const redirectSessionExpired = () => {
    showToast(t('users.sessionExpired'), "info");
    setTimeout(() => {
      showToast(t('users.redirecting'), "info");
      setTimeout(() => SignOut("/admin/signup"), 5000);
    }, 2500);
  };

  const handleApiError = (error: any, options: ErrorHandlerOptions = {}) => {
    const { setLoading } = options;
    console.error(error);

    if (error?.response?.status === 401 || error?.response?.data?.error?.includes("token")) {
      return redirectSessionExpired();
    }

    showToast(
      getApiErrorMessage(error, "Erro inesperado ao processar sua solicitação"),
      "error",
      8000
    );
    if (setLoading) setLoading(false);
  };

  const closeLicenseDisclaimer = () => {
    setShowLicenseDisclaimer(false);
    setLicenseDisclaimerMode(null);
    setPendingReactivateUser(null);
  };

  const reloadUsersAndGroups = async (companyId: string) => {
    const usersResult = await searchUsers({ company_id: companyId, statuses: [1, 2, 3, 4, 5] });
    const groupsResult = await searchGroups({ company_id: companyId });

    setUserList(usersResult.data);
    setGroupList(groupsResult.data);
    setListData({
      total_items: usersResult.total_items,
      total_pages: 5,
      current_page: 1,
      data: usersResult.data,
    });
  };

  const refreshCompanyAndReloadData = async () => {
    const refreshedCompany = await refreshCurrentCompany();
    const companyId = refreshedCompany?.id ?? currentCompanyId;

    if (!companyId) return;

    await reloadUsersAndGroups(companyId);
  };

  const reconcileUserAfterToggle = (updatedUser: any) => {
    if (!updatedUser?.id) return;

    const updatedUserId = getEntityId(updatedUser.id);

    const mergeUpdatedUser = (items: User[]) =>
      items.map((item) =>
        getEntityId(item.id) === updatedUserId ? { ...item, ...updatedUser } : item
      );

    setUserList((prev) => mergeUpdatedUser(prev));
    setListData((prev) => ({
      ...prev,
      data: mergeUpdatedUser(prev.data),
    }));
  };

  const refreshAfterStatusToggle = async (updatedUser?: any) => {
    if (updatedUser) {
      reconcileUserAfterToggle(updatedUser);
    }

    await refreshCompanyAndReloadData();
  };

  const buildRequesterDisplayName = (requesterUser: Partial<User> | null | undefined) => {
    const firstName = (requesterUser?.first_name ?? "").trim();
    const lastName = (requesterUser?.last_name ?? "").trim();
    const email = (requesterUser?.email ?? "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (!fullName && !email) {
      return "Usuário não identificado";
    }

    if (!fullName) {
      return email;
    }

    if (!email) {
      return fullName;
    }

    return `${fullName} (${email})`;
  };

  const resolveRequesterDisplayName = useCallback(
    async (requesterUserId: string) => {
      const normalizedRequesterId = getEntityId(requesterUserId);

      if (!normalizedRequesterId) {
        return "Usuário não identificado";
      }

      const cachedLabel = requesterDisplayNameCacheRef.current[normalizedRequesterId];
      if (cachedLabel) {
        return cachedLabel;
      }

      const pendingRequest = requesterDisplayNamePendingRef.current[normalizedRequesterId];
      if (pendingRequest) {
        return pendingRequest;
      }

      const localRequester =
        userList.find((item) => getEntityId(item.id) === normalizedRequesterId) ??
        listData.data.find((item) => getEntityId(item.id) === normalizedRequesterId) ??
        null;

      if (localRequester) {
        const localLabel = buildRequesterDisplayName(localRequester);
        requesterDisplayNameCacheRef.current[normalizedRequesterId] = localLabel;
        return localLabel;
      }

      const pendingPromise = (async () => {
        try {
          const apiResponse = await getUserById(normalizedRequesterId);
          const apiUser =
            apiResponse?.Item ??
            apiResponse?.user ??
            apiResponse?.data ??
            apiResponse ??
            null;
          const apiLabel = buildRequesterDisplayName(apiUser);

          if (apiLabel !== "Usuário não identificado") {
            requesterDisplayNameCacheRef.current[normalizedRequesterId] = apiLabel;
          }

          return apiLabel;
        } catch (error) {
          console.error("Erro ao resolver o usuário solicitante:", error);
          return "Usuário não identificado";
        } finally {
          delete requesterDisplayNamePendingRef.current[normalizedRequesterId];
        }
      })();

      requesterDisplayNamePendingRef.current[normalizedRequesterId] = pendingPromise;
      return pendingPromise;
    },
    [listData.data, userList]
  );

  const openAddUserDisclaimer = () => {
    setLicenseDisclaimerMode("add-user");
    setShowLicenseDisclaimer(true);
  };

  const openReactivateUserDisclaimer = (userToReactivate: UserTableModel) => {
    setPendingReactivateUser(userToReactivate);
    setLicenseDisclaimerMode("reactivate-user");
    setShowLicenseDisclaimer(true);
  };

  const handleAddUserRequest = () => {
    if (availableLicenses === 0) {
      openAddUserDisclaimer();
      return;
    }

    setShowModal(true);
  };

  const executeToggleUserStatus = async (userToToggle: UserTableModel) => {
    try {
      if (!user?.user?.id) {
        showToast(t('users.requesterMissing'), "error", 5000);
        return;
      }

      const response = await toggleUserStatus({
        id: userToToggle.id,
        informedRequesterId: user.user.id,
        currentUserStatus: userToToggle.status,
      });

      showToast(response?.message || "Usuário alterado com sucesso!", "success", 3000);
      await refreshAfterStatusToggle(response?.user);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        showToast(
          "Este usuário já foi atualizado por outra ação. A lista será recarregada.",
          "warning",
          5000
        );
        await refreshAfterStatusToggle();
        return;
      }

      if (error?.response?.status === 404) {
        showToast(t('users.notFound'), "error", 5000);
        return;
      }

      if (error?.response?.status === 400) {
        const backendMessage = getApiErrorMessage(
          error,
          "Não foi possível validar a solicitação."
        );

        showToast(backendMessage, "error", 6000);
        return;
      }

      handleApiError(error);
    } finally {
      handleCloseUser();
    }
  };

  const handleToggleSelectedUserStatus = async () => {
    if (!selectedUser?.id) return;

    const isReactivation =
      selectedUser.status === UserStatus.inactive ||
      selectedUser.status === UserStatus.invited_inactive;

    if (isReactivation && availableLicenses === 0) {
      openReactivateUserDisclaimer(selectedUser);
      handleCloseUser();
      return;
    }

    await executeToggleUserStatus(selectedUser);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!currentCompanyId) return redirectSessionExpired();
        await reloadUsersAndGroups(currentCompanyId);
      } catch (e: any) {
        handleApiError(e);
      } finally {
        setLoadingTable(false);
      }
    };

    loadData();
    setThereIsUpdates(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompanyId, thereIsUpdates]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTerm(term);
    }, 400);

    return () => clearTimeout(timeout);
  }, [term]);

  const InitialUserList = useMemo(() => {
    if (debouncedTerm === "") {
      return userList;
    }
    return [];
  }, [userList, debouncedTerm]);

  useEffect(() => {
    const fetchUsersBySearch = async () => {
      if (!currentCompanyId) return;

      if (selectedInactivationReasonCodes.length > 0) {
        setControlLoading(true);

        try {
          const response = await searchUsers({
            company_id: currentCompanyId,
            term: debouncedTerm || undefined,
            statuses: [3, 4, 5],
            inactivationReasonCodes: selectedInactivationReasonCodes,
          });

          setListData({
            total_items: response.total_items,
            total_pages: Math.ceil(response.total_items / 10),
            current_page: 1,
            data: response.data,
          });
        } catch (error) {
          console.error("Erro ao buscar usuários:", error);
        } finally {
          setControlLoading(false);
        }

        return;
      }

      if (debouncedTerm === "") {
        setListData({
          total_items: InitialUserList.length,
          total_pages: Math.ceil(InitialUserList.length / 10),
          current_page: 1,
          data: InitialUserList,
        });
        return;
      }

      setControlLoading(true);

      try {
        const response = await searchUsers({
          company_id: currentCompanyId,
          term: debouncedTerm,
          statuses: [1, 2, 3, 4, 5],
        });

        setListData({
          total_items: response.total_items,
          total_pages: Math.ceil(response.total_items / 10),
          current_page: 1,
          data: response.data,
        });
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      } finally {
        setControlLoading(false);
      }
    };

    fetchUsersBySearch();
  }, [debouncedTerm, currentCompanyId, InitialUserList, selectedInactivationReasonCodes]);

  const findUserByInput = async () => {
    if (!term)
      return;

    setDebouncedTerm(term);
  };

  const addItemToListData = async (newItem: any) => {
    try {
      await registerUser(newItem);
      await refreshCompanyAndReloadData();
      return true;
    } catch (error: any) {
      throw error;
    }
  };

  const handleClickUsers = (event: React.MouseEvent<HTMLElement>, userSelected: UserTableModel) => {
    setAnchorElUser(event.currentTarget);
    setSelectedUser(userSelected);
  };

  const handleCloseUser = () => {
    setAnchorElUser(null);
    setSelectedUser(null);
  };

  const openUserInactivationModal = async (userRow: UserTableModel) => {
    const isReactivation =
      userRow.status === UserStatus.inactive ||
      userRow.status === UserStatus.invited_inactive;

    if (isReactivation) {
      if (availableLicenses === 0) {
        openReactivateUserDisclaimer(userRow);
        handleCloseUser();
        return;
      }

      await executeToggleUserStatus(userRow);
      return;
    }

    setUserToChangeStatus(userRow);
    setShowInactivationModal(true);
    handleCloseUser();
  };

  const closeUserInactivationModal = () => {
    setShowInactivationModal(false);
    setUserToChangeStatus(null);
  };

  const handleEditUser = (userToEdit: User) => {
    setEditUser(userToEdit);
    setEditModalOpen(true);
  };

  const findFreshUserById = (userId?: string | null) => {
    if (!userId) return null;

    const normalizedId = getEntityId(userId);

    return (
      listData.data.find((item) => getEntityId(item.id) === normalizedId) ??
      userList.find((item) => getEntityId(item.id) === normalizedId) ??
      null
    );
  };

  const openUserGroupsModal = (userRow: UserTableModel) => {
    const userFull = findFreshUserById(userRow.id) ?? userRow.rawUser ?? null;

    if (!userFull) {
      showToast(t('users.selectedMissing'), "error", 5000);
      return;
    }

    setModalGroupUser(userFull);
    setShowModalGroup(true);
  };

  const openEditUserModal = (userRow: UserTableModel) => {
    const userFull = findFreshUserById(userRow.id) ?? userRow.rawUser ?? null;

    if (!userFull) {
      showToast(t('users.selectedMissing'), "error", 5000);
      return;
    }

    handleEditUser(userFull);
  };

  const handleResendInvite = async () => {
    if (!selectedUser?.email) return;

    try {
      await resendConfirmationCode(selectedUser.email);
      showToast(t('users.inviteSent'), "info", 3000);
    } catch (error) {
      console.error("Erro ao reenviar convite por e-mail:", error);
      showToast(t('users.inviteFailed'), "error", 5000);
    } finally {
      handleCloseUser();
    }
  };

  const handleResendReminder = async () => {
    if (!selectedUser?.email) return;

    try {
      await sendSurveys({ address: selectedUser.email });
      showToast(t('users.reminderSent'), "info", 3000);
    } catch (error) {
      console.error("Erro ao reenviar lembrete por e-mail:", error);
      showToast(t('users.reminderFailed'), "error", 5000);
    } finally {
      handleCloseUser();
    }
  };

  const toggleStatusFilter = (value: StatusFilterValue) => {
    setFilterModel((prev) => {
      const currentStatusItem = prev.items.find((item) => item.field === "status");
      const currentStatusValues =
        currentStatusItem?.operator === "isAnyOf" && Array.isArray(currentStatusItem.value)
          ? currentStatusItem.value.filter(
              (item): item is StatusFilterValue =>
                item === "active" || item === "invited" || item === "inactive"
            )
          : [];

      const remainingItems = prev.items.filter((item) => item.field !== "status");
      const shouldClearFilter = currentStatusValues.includes(value);

      return {
        ...prev,
        items: shouldClearFilter
          ? remainingItems
          : [
              ...remainingItems,
              {
                id: "status-filter",
                field: "status",
                operator: "isAnyOf",
                value: [value],
              },
            ],
      };
    });
  };

  const handleGridFilterModelChange = (newModel: GridFilterModel) => {
    setFilterModel(newModel);
  };

  return (
    <div style={{ padding: "0 1.2em" }}>
      <ContainerConfigUser>
        <InputWrapper>
          {showGeneralLoadingSkeleton ? (
            <Skeleton
              variant="rounded"
              animation="wave"
              height={56}
              sx={{
                display: "block",
                width: "90%",
                maxWidth: "100%",
                "@media (max-width:694px)": {
                  width: "100%",
                },
              }}
            />
          ) : (
            <TextField
              variant="outlined"
              value={term}
              label={String(t('users.searchLabel'))}
              placeholder={String(t('users.searchPlaceholder'))}
              disabled={loadingTable}
              onChange={(e) => {
                setTerm(e.target.value);
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={findUserByInput}>
                    <Search />
                  </IconButton>
                ),
              }}
                sx={{
                  width: "90%",
                  color: "#3C4043",
                  backgroundColor: "#FFF",
                  borderRadius: "0.625rem",
                  "@media (max-width:694px)": {
                    width: "100%",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: term.length > 0 ? "#c9c9c9" : "transparent",
                    borderRadius: "6px",
                    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.05)",
                  },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c9c9c9",
                  borderRadius: "1rem",
                },
                "& .MuiOutlinedInput-input": {
                  cursor: loadingTable ? "wait" : "text",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c9c9c9",
                },
                "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c9c9c9",
                },
                "@media (max-width:1440px)": {
                  width: "100%",
                  fontSize: "14px",
                },
              }}
            />
          )}
        </InputWrapper>

        <ContainerFilterButton>
          <UserCounters
            activeUsers={activeUsers}
            invitedUsers={invitedUsers}
            inactiveUsers={inactiveUsers}
            availableLicenses={availableLicenses}
            showSkeleton={showGeneralLoadingSkeleton}
            onAddUser={handleAddUserRequest}
            onToggleActiveFilter={() => toggleStatusFilter("active")}
            onToggleInvitedFilter={() => toggleStatusFilter("invited")}
            onToggleInactiveFilter={() => toggleStatusFilter("inactive")}
            isActiveFilterApplied={isStatusFilterSelected("active")}
            isInvitedFilterApplied={isStatusFilterSelected("invited")}
            isInactiveFilterApplied={isStatusFilterSelected("inactive")}
          />
        </ContainerFilterButton>
      </ContainerConfigUser>

      <div style={{ marginTop: "1em", overflowX: "auto" }}>
        {loadingTable ? (
          <TableSkeleton columns={5} rows={8} />
        ) : (
          <UserTable
            users={listData.data.map((item) => ({
              id: item.id,
              first_name: item.first_name,
              last_name: item.last_name,
              role: item.role,
              email: item.email,
              cellphone: item.cellphone,
              birth_date: item.birth_date,
              sex: item.sex,
              country: item.country,
              responsible_groups: item.responsible_groups,
              group: {
                name:
                  item?.group
                    ?.map((groupId) => groupList.find((group) => group.id === groupId)?.name || null)
                    ?.filter(Boolean)
                    ?.join(", ") || "",
              },
              created_at: item.created_at,
              updated_at: item.updated_at,
              status: item.status,
              last_inactivation: item.last_inactivation ?? null,
              rawUser: item,
            }))}
            totalItems={listData.data.length}
            totalPages={Math.ceil(listData.data.length / 10)}
            currentPage={listData.current_page}
            onPageChange={() => {}}
            filterModel={filterModel}
            onFilterModelChange={handleGridFilterModelChange}
            anchorEl={anchorElUser}
            handleClick={handleClickUsers}
            selectedUser={selectedUser}
            handleClose={handleCloseUser}
            openMenu={openUser}
            onResendInvite={handleResendInvite}
            onResendReminder={handleResendReminder}
            resolveRequesterDisplayName={resolveRequesterDisplayName}
            exportFileCompanyName={currentCompany?.name_company ?? user.company_visited?.name_company}
            allGroups={groupList}
            showModal={(userRow) => {
              openUserGroupsModal(userRow);
            }}
            showEditModal={(userRow) => {
              openEditUserModal(userRow);
            }}
            onInactivate={openUserInactivationModal}
          />
        )}
      </div>

      <ModalAddUser
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setThereIsUpdates(true);
        }}
        addItem={addItemToListData}
      />

      <LicenseDisclaimerModal
        open={showLicenseDisclaimer}
        title={licenseDisclaimerMode === "reactivate-user" ? t('users.licenseReactivateTitle') : t('users.licenseAddTitle')}
        description={
          licenseDisclaimerMode === "reactivate-user"
            ? t('users.licenseReactivateWarning') : t('users.licenseAddWarning')
        }
        confirmLabel={String(t('users.confirm'))}
        cancelLabel={String(t('users.cancel'))}
        onCancel={closeLicenseDisclaimer}
        onConfirm={async () => {
          if (licenseDisclaimerMode === "reactivate-user" && pendingReactivateUser?.id) {
            closeLicenseDisclaimer();
            await executeToggleUserStatus(pendingReactivateUser);
            return;
          }

          if (licenseDisclaimerMode === "add-user") {
            closeLicenseDisclaimer();
            setShowModal(true);
          }
        }}
      />

      <ModalUserGroups
        key={modalGroupUser?.id ?? "modal-user-groups-empty"}
        show={showModalGroup}
        onClose={() => {
          setModalGroupUser(null);
          setShowModalGroup(false);
          setThereIsUpdates(true);
        }}
        user={modalGroupUser}
        allGroups={groupList}
      />

      <ModalUserInactivationReason
        show={showInactivationModal}
        onClose={closeUserInactivationModal}
        userInfo={userToChangeStatus}
        onSuccess={async (updatedUser) => {
          await refreshAfterStatusToggle(updatedUser);
        }}
        onConflict={async () => {
          await refreshAfterStatusToggle();
        }}
      />

      <ModalEditUser
        key={editUser?.id ?? "modal-edit-user-empty"}
        show={isEditModalOpen}
        onClose={() => {
          setEditUser(null);
          setEditModalOpen(false);
          setThereIsUpdates(true);
        }}
        userInfo={editUser}
      />
    </div>
  );
}
